/**
 * 离线缓存（design §4.2，2026-10-02 用户确认要做）：缩略图和上次同步的元数据存在这台设备的 Cache Storage 里，
 * 断网、服务停了的时候也能看星海、看见过的照片和回忆的文字。原图和数据仍以服务端为准，这里只是随时可以清空的缓存。
 *
 * - 缩略图（128、512 两档）：同一张照片的同一档不会变，所以先查缓存、没有再取；各自限量（4000 张 128、400 张 512，
 *   合起来几十 MB），超了按存进去的先后删掉最早的。主线程（照片面板、轮转）和缩略图 Worker 都走 thumbFetch。
 * - 元数据：最近一次同步的快照（星海、成员、回忆、照片清单）和自己的身份，打开时连不上服务就先显示它。
 * - 设备令牌失效（这台设备被移除）时全部清掉；顶栏的连接状态里也能手动清空。
 * 接口和原图不缓存；没有 Cache Storage 的环境（比如不是 https 的局域网地址）照常联网取。
 */
import { photoPath, type MeResponse, type SnapshotResponse } from '@star-sea/shared';

export const CACHE_NAMES = { t128: 'star-sea-thumbs-128', t512: 'star-sea-thumbs-512', data: 'star-sea-data' } as const;
export const LIMITS = { t128: 4000, t512: 400 } as const;
/** 每存这么多张检查一次有没有超量 */
const TRIM_EVERY = 40;
/** 快照在缓存里的键：不是真的接口，服务端不会有这个地址 */
const SNAPSHOT_KEY = '/offline/snapshot.json';

type ThumbSize = keyof typeof LIMITS;

export interface OfflineSnapshot {
  /** 存的时间（ISO） */
  savedAt: string;
  me: MeResponse;
  snapshot: SnapshotResponse;
}

/** 取图失败时带上 HTTP 状态码；0 是网络不通 */
export class FetchError extends Error {
  readonly status: number;
  constructor(status: number) {
    super(status ? `取图失败（${status}）` : '连不上星海服务');
    this.status = status;
  }
}

const supported = () => typeof caches !== 'undefined';

/** 这个网址是哪一档缩略图；不是缩略图返回 null */
export function thumbSize(url: string): ThumbSize | null {
  const m = /\/(t128|t512)(?:[?#]|$)/.exec(url);
  return m ? (m[1] as ThumbSize) : null;
}

/** trim 只用到缓存的这两个方法，测试里可以换成假的 */
export interface CacheLike {
  keys(): Promise<readonly Request[]>;
  delete(request: Request): Promise<boolean>;
}

/** 超过 max 条时，按存进去的先后删掉最早的，删到 max 的九成（免得每存一张就删一张）；返回删了几条 */
export async function trim(cache: CacheLike, max: number): Promise<number> {
  const keys = await cache.keys();
  if (keys.length <= max) return 0;
  const drop = keys.length - Math.floor(max * 0.9);
  for (let k = 0; k < drop; k++) await cache.delete(keys[k]!);
  return drop;
}

const puts: Record<ThumbSize, number> = { t128: 0, t512: 0 };

/** 取缩略图：先查缓存，没有再联网取，取到了存一份。缓存里没有、网络也不通时抛 FetchError */
export async function thumbFetch(url: string, token: string | null, signal?: AbortSignal): Promise<Blob> {
  const size = thumbSize(url);
  let cache: Cache | null = null;
  if (size && supported()) {
    try {
      cache = await caches.open(CACHE_NAMES[size]);
      const hit = await cache.match(url);
      if (hit) return await hit.blob();
    } catch {
      // 隐私模式等情况下缓存用不了：照常联网
      cache = null;
    }
  }
  let res: Response;
  try {
    res = await fetch(url, { headers: token ? { authorization: `Bearer ${token}` } : {}, signal });
  } catch (e) {
    if (signal?.aborted) throw e;
    throw new FetchError(0);
  }
  if (!res.ok) throw new FetchError(res.status);
  if (cache && size) {
    const c = cache, n = ++puts[size];
    // 存缓存不耽误显示；存不下（配额满了）就算了
    void c
      .put(url, res.clone())
      .then(() => (n % TRIM_EVERY === 0 ? trim(c, LIMITS[size]) : 0))
      .catch(() => undefined);
  }
  return res.blob();
}

/** 存一份最近同步的快照，下次连不上服务时先显示它 */
export async function saveSnapshot(me: MeResponse, snapshot: SnapshotResponse): Promise<void> {
  if (!supported()) return;
  try {
    const cache = await caches.open(CACHE_NAMES.data);
    const body: OfflineSnapshot = { savedAt: new Date().toISOString(), me, snapshot };
    await cache.put(SNAPSHOT_KEY, new Response(JSON.stringify(body), { headers: { 'content-type': 'application/json' } }));
  } catch {
    // 存不了就不存，联网时照常用
  }
}

export async function loadSnapshot(): Promise<OfflineSnapshot | null> {
  if (!supported()) return null;
  try {
    const hit = await (await caches.open(CACHE_NAMES.data)).match(SNAPSHOT_KEY);
    return hit ? ((await hit.json()) as OfflineSnapshot) : null;
  } catch {
    return null;
  }
}

/** 照片永久删除了：这台设备上缓存的那两档缩略图也删掉，不留在手机里 */
export async function forgetThumbs(id: string): Promise<void> {
  if (!supported()) return;
  await Promise.all(
    (['t128', 't512'] as const).map(async (size) => {
      try {
        await (await caches.open(CACHE_NAMES[size])).delete(photoPath(id, size));
      } catch {
        // 缓存用不了就算了
      }
    }),
  );
}

/** 清掉这台设备上的离线缓存（缩略图和快照） */
export async function clearOffline(): Promise<void> {
  if (!supported()) return;
  await Promise.all(Object.values(CACHE_NAMES).map((name) => caches.delete(name).catch(() => false)));
}

/** 缓存了多少张缩略图、快照是什么时候存的 */
export async function offlineStats(): Promise<{ thumbs: number; savedAt: string | null }> {
  if (!supported()) return { thumbs: 0, savedAt: null };
  try {
    let thumbs = 0;
    for (const name of [CACHE_NAMES.t128, CACHE_NAMES.t512]) if (await caches.has(name)) thumbs += (await (await caches.open(name)).keys()).length;
    return { thumbs, savedAt: (await loadSnapshot())?.savedAt ?? null };
  } catch {
    return { thumbs: 0, savedAt: null };
  }
}
